class CurrencyFormatter {
  constructor(locale, currency, isEstimated = false) {
    this.isConverted = isEstimated;
    this.formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    });
  }

  toMajor(minor) {
    const major = minor / 10 ** this.formatter.resolvedOptions().maximumFractionDigits;
    return this.formatter.format(major);
  }

  renderHtml(minor, opts) {
    const isStruck = Boolean(opts?.isStruck ?? false);
    const isEstimated = Boolean(opts?.isConverted ?? this.isConverted ?? false);

    let output = this.toMajor(minor);
    if (isStruck) output = `<s>${output}</s>`;
    if (isEstimated) output = `<abbr title="Converted Estimate">${output}</abbr>`;
    return output;
  }
}

/**
 * @typedef {{ total_price: number, currency: string }} Cart
 * @typedef {{ active: string, rate: string }} CartCurrency
 * @typedef {{ from: number, price: number }} Tier
 * @typedef {{ market: string, currency: string, tiers: Tier[], isEstimated?: boolean }} MarketShippingRate
 */

// noinspection CssUnresolvedCustomProperty
class LbShippingProgress extends HTMLElement {
  #prevProgress;
  static get observedAttributes() {
    return ['data-cart', 'data-market', 'data-locale', 'data-i18n', 'data-shipping-rates', 'data-base-currency'];
  }

  attributeChangedCallback() {
    this.render();
  }

  connectedCallback() {
    if (!this.shadowRoot) {
      this.attachShadow({ mode: 'open' });
      this.shadowRoot.adoptedStyleSheets = [this.resolveStyles()];
    }
    this.render();
  }

  render() {
    if (!this.shadowRoot) return;

    /** @type {string} */
    const market = this.dataset.market ?? 'international';
    /** @type {Cart} */
    const cart = JSON.parse(this.dataset.cart);
    /** @type {CartCurrency} */
    const currency = window.Shopify.currency;
    if (!currency) return;

    const i18n = JSON.parse(this.dataset.i18n ?? 'null');
    if (!i18n) return;

    const shippingRates = JSON.parse(this.dataset.shippingRates ?? 'null');
    if (!shippingRates) return;

    const baseCurrency = this.dataset.baseCurrency;
    const resolvedRates = this.resolveRates(shippingRates, market, currency, baseCurrency);
    if (!resolvedRates) return;
    const progress = this.resolveProgress(resolvedRates.tiers, cart);
    const fmt = new CurrencyFormatter(this.dataset.locale, resolvedRates.currency, resolvedRates.isEstimated ?? false);

    const isPopup = this.dataset.context === 'popup';
    const label = isPopup ? i18n.estShippingPopup : i18n.estShipping;

    const nudgeText = i18n[progress.messageType];
    const nudge = nudgeText
      ?.replace(':amount', fmt.renderHtml(progress.nextThreshold - cart.total_price))
      ?.replace(':price', progress.nextTier?.price === 0 ? i18n.free : fmt.renderHtml(progress.nextTier?.price));

    const messageHtml = (() => {
      if (!label) return '';
      const parts = [];
      if (progress.prevTier) parts.push(fmt.renderHtml(progress.prevTier.price, { isStruck: true }));
      const currentPrice = (() => {
        if (progress.currentTier.price === 0) return i18n.free;
        if (isPopup) return fmt.toMajor(progress.currentTier.price);
        const text = `+ ${fmt.toMajor(progress.currentTier.price)} ${resolvedRates.currency}`;
        return fmt.isConverted ? `<abbr title="Converted Estimate">${text}</abbr>` : text;
      })();
      parts.push(currentPrice);
      return `<div class="lb-shipping-progress__message">
        <span class="lb-shipping-progress__message-label">${label}</span>
        <span class="lb-shipping-progress__message-value">${parts.join(' ')}</span>
      </div>`;
    })();

    const barHtml = (() => {
      const left =
        progress.currentTier.from > 0 && progress.currentTier.price > 0
          ? fmt.renderHtml(progress.currentTier.from)
          : '';
      const right = fmt.renderHtml(progress.nextTier !== null ? progress.nextTier.from : progress.currentTier.from);

      return `<div class="lb-shipping-progress__bar">
        ${left ? `<span class="lb-shipping-progress__bar-left">${left}</span>` : ''}
        <div class="lb-shipping-progress__bar-track">
          <div class="lb-shipping-progress__bar-fill" style="width: ${progress.progressPct}%"></div>
        </div>
        ${right ? `<span class="lb-shipping-progress__bar-right">${right}</span>` : ''}
      </div>`;
    })();

    const prevProgress = this.#prevProgress ?? progress.progressPct;
    this.#prevProgress = progress.progressPct;

    this.setAttribute('role', 'region');
    this.setAttribute('aria-label', label);

    this.shadowRoot.innerHTML = `
      ${messageHtml}
      ${barHtml}
      ${nudge ? `<p class="lb-shipping-progress__nudge">${nudge}</p>` : ''}
    `;

    this.shadowRoot
      .querySelector('.lb-shipping-progress__bar-fill')
      ?.animate([{ width: `${prevProgress}%`, offset: 0 }], {
        delay: this.dataset.context === 'popup' ? 200 : 0,
        duration: 200,
        easing: 'ease',
        fill: 'backwards',
      });
  }

  resolveStyles() {
    const sheet = new CSSStyleSheet();
    // language=CSS
    sheet.replaceSync(`
        :host {
            display: block;
        }

        :host([data-context="page"]) {
            margin: 2.2rem 0;
        }

        :host([data-context="popup"]) {
            padding-bottom: 2rem;
        }

        .lb-shipping-progress__message {
            display: flex;
            align-items: baseline;
            gap: 1.25em;
            margin: 0 0 0.5em;
            line-height: 1;
        }

        :host([data-context="page"]) .lb-shipping-progress__message {
            justify-content: flex-end;
        }

        :host([data-context="popup"]) .lb-shipping-progress__message {
            justify-content: center;
        }

        .lb-shipping-progress__message-label,
        .lb-shipping-progress__message-value {
            color: rgb(var(--color-foreground));
        }

        :host([data-context="page"]) .lb-shipping-progress__message-label {
            font-family: var(--font-heading-family);
            font-style: var(--font-heading-style);
            font-weight: var(--font-heading-weight);
            font-size: 1.6rem;
        }

        :host([data-context="page"]) .lb-shipping-progress__message-value {
            font-size: 1.8rem;
            color: rgba(var(--color-foreground), 0.75);
        }

        .lb-shipping-progress__bar {
            display: flex;
            align-items: center;
            gap: 0.75em;
        }

        .lb-shipping-progress__bar-left,
        .lb-shipping-progress__bar-right {
            color: rgb(var(--color-foreground));
            white-space: nowrap;
            font-size: 0.85em;
            line-height: calc(1 + 0.5 / var(--font-body-scale));
            letter-spacing: 0.04rem;
        }

        .lb-shipping-progress__bar-track {
            flex: 1;
            height: 0.875em;
            border-radius: var(--buttons-radius, 0.4rem);
            background: rgba(var(--color-foreground), 0.08);
            box-shadow: 0 0 0 var(--buttons-border-width, 0.1rem) rgba(var(--color-foreground), 0.1);
            overflow: hidden;
        }

        .lb-shipping-progress__bar-fill {
            height: 100%;
            border-radius: inherit;
            background-color: rgba(var(--color-button), var(--alpha-button-background, 1));
        }

        .lb-shipping-progress__nudge {
            color: rgb(var(--color-foreground));
            margin: 0.5em 0 0;
            font-size: 0.85em;
            line-height: calc(1 + 0.5 / var(--font-body-scale));
            letter-spacing: 0.04rem;
        }

        :host([data-context="page"]) .lb-shipping-progress__nudge {
            text-align: right;
        }

        :host([data-context="popup"]) .lb-shipping-progress__nudge {
            text-align: center;
            font-size: 1.4rem;
            color: rgba(var(--color-foreground), 0.75);
        }
    `);
    return sheet;
  }

  /**
   * @param {MarketShippingRate[]} marketShippingRates
   * @param {string} market
   * @param {{active: string, rate: number}} currency
   * @param {string} baseCurrency
   * @returns {(MarketShippingRate&{isEstimated?: boolean})|null}
   */
  resolveRates(marketShippingRates, market, currency, baseCurrency) {
    const marketRate =
      marketShippingRates.find((r) => r.market === market && r.currency === currency.active) ??
      marketShippingRates.find((r) => r.market === market) ??
      marketShippingRates.find((r) => r.market === 'international');

    if (marketRate && marketRate.currency.toLowerCase() === currency.active.toLowerCase()) return marketRate;
    if (!marketRate || marketRate.currency !== baseCurrency) return null;

    /** @param {string} currency */
    const minorFactor = (currency) =>
      new Intl.NumberFormat([], { style: 'currency', currency: currency }).resolvedOptions().maximumFractionDigits;

    const baseMinorFactor = 10 ** minorFactor(baseCurrency);
    const activeMinorFactor = 10 ** minorFactor(currency.active);
    const rate = Number(currency.rate);

    const convert = (minor) => Math.ceil(((minor / baseMinorFactor) * rate * activeMinorFactor) / 250) * 250;

    return {
      market,
      currency: currency.active,
      isEstimated: true,
      tiers: marketRate.tiers.map((tier) => ({
        from: tier.from > 0 ? convert(tier.from) : 0,
        price: tier.price > 0 ? convert(tier.price) : 0,
      })),
    };
  }

  /**
   * @param {Tier[]} tiers
   * @param {Cart} cart
   */
  resolveProgress(tiers, cart) {
    const currentTierIndex = tiers.findIndex((tier) => cart.total_price >= tier.from);

    const prevTier = tiers[currentTierIndex + 1] ?? null;
    const currentTier = tiers[currentTierIndex];
    const nextTier = currentTierIndex > 0 ? tiers[currentTierIndex - 1] : null;

    const currentThreshold = currentTier.from;
    const nextThreshold = nextTier?.from ?? null;

    const progressPct = (() => {
      if (nextThreshold === null) return 100;
      if (nextThreshold === currentThreshold) return 0;
      return Math.round(
        Math.min(((cart.total_price - currentThreshold) / (nextThreshold - currentThreshold)) * 100, 100),
      );
    })();

    const messageType = (() => {
      if (nextTier === null) return currentTier.price === 0 ? 'haveFree' : 'haveReduced';
      if (nextTier.price === 0) return 'addForFree';
      return 'addForReduced';
    })();

    return {
      progressPct,
      nextThreshold,
      messageType,
      currentTier,
      nextTier,
      prevTier,
    };
  }
}

customElements.define('lb-shipping-progress', LbShippingProgress);

if (typeof subscribe === 'function') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    const cartData = event?.cartData;
    let total_price, currency;

    if (typeof cartData?.total_price === 'number') {
      total_price = cartData.total_price;
      currency = cartData.currency;
    } else if (cartData?.sections?.['lb-shipping-progress']) {
      // add-to-cart responses carry section HTML instead of cart totals — parse it out
      const sectionEl = new DOMParser()
        .parseFromString(cartData.sections['lb-shipping-progress'], 'text/html')
        .querySelector('lb-shipping-progress');
      if (!sectionEl) return;
      ({ total_price, currency } = JSON.parse(sectionEl.dataset.cart));
    } else {
      return;
    }

    document.querySelectorAll('lb-shipping-progress').forEach((el) => {
      el.dataset.cart = JSON.stringify({ total_price, currency });
    });
  });
}
